/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { ReportsLoadErrorRow } from './ReportsLoadErrorRow';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { supabase } from '../../../shared/supabase/supabaseClient';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { formatMoney } from '../../../shared/format/public-api';

type ChannelRow = {
  channel: string;
  total: number;
  count: number;
};

export function ChannelBreakdownSection({ from, to }: { from: string; to: string }) {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!currentBranchId) return;
    setLoading(true);
    setLoadError(false);
    // RPC vive en schema `pos` (no public). Sin `.schema('pos')` PostgREST no lo resuelve.
    Promise.resolve(
      supabase
        .schema('pos')
        .rpc('sales_channel_breakdown', {
          p_branch_id: currentBranchId,
          p_from: from,
          p_to: to,
        })
        .then(({ data, error }) => {
          if (error) {
            console.warn('ChannelBreakdownSection', error);
            setRows([]);
            setLoadError(true);
          } else {
            setRows((data ?? []) as ChannelRow[]);
          }
        }),
    ).finally(() => setLoading(false));
  }, [currentBranchId, from, to, reloadTick]);

  if (!currentBranchId) return null;

  const posRow = rows.find((r) => r.channel === 'pos');
  const onlineRow = rows.find((r) => r.channel === 'online');
  const grandTotal = (posRow?.total ?? 0) + (onlineRow?.total ?? 0);
  const onlinePct = grandTotal > 0 ? Math.round(((onlineRow?.total ?? 0) / grandTotal) * 100) : 0;

  return (
    <View style={{ marginTop: spacing.md }}>
      <ReportsBlockHeading label="Ventas por canal" />
      <ReportsDenseListCard>
        {loading ? (
          <Text style={styles.caption}>Cargando…</Text>
        ) : loadError ? (
          <ReportsLoadErrorRow
            message="No se pudieron cargar las ventas por canal."
            onRetry={() => setReloadTick((t) => t + 1)}
          />
        ) : rows.length > 0 ? (
          <>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Mostrador</Text>
              <Text style={styles.reportsDenseListFieldValue}>
                {formatMoney(Math.round(posRow?.total ?? 0))} ({posRow?.count ?? 0} ventas)
                {(posRow?.count ?? 0) > 0
                  ? ` · ticket ${formatMoney(Math.round((posRow!.total / posRow!.count)))}`
                  : ''}
              </Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Pedidos por internet</Text>
              <Text style={[styles.reportsDenseListFieldValue, { color: ticketColors.gold }]}>
                {formatMoney(Math.round(onlineRow?.total ?? 0))} ({onlineRow?.count ?? 0} ventas)
                {(onlineRow?.count ?? 0) > 0
                  ? ` · ticket ${formatMoney(Math.round((onlineRow!.total / onlineRow!.count)))}`
                  : ''}
              </Text>
            </View>
            <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: 1, borderTopColor: '#E0E0E0', paddingTop: spacing.xs, marginTop: spacing.xs }]}>
              <Text style={styles.reportsDenseListFieldLabel}>Total del período</Text>
              <Text style={styles.reportsDenseListFieldValue}>
                {formatMoney(Math.round(grandTotal))}
              </Text>
            </View>
            <Text style={[styles.caption, { marginTop: spacing.xs }]}>
              Pedidos por internet: {onlinePct}% del total
            </Text>
          </>
        ) : (
          <Text style={styles.caption}>Sin ventas en el período.</Text>
        )}
      </ReportsDenseListCard>
    </View>
  );
}
